import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { currencyAPI } from '@/api/currency';

interface CurrencyRates {
  usd: number;
  eur: number;
  date: string;
}

export const useCurrencyRates = () => {
  const [rates, setRates] = useState<CurrencyRates | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRequestedDate, setLastRequestedDate] = useState<string | null>(null);

  // Format the date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Fetch rates from the API forcefully
  const fetchRates = useCallback(async (date: Date) => {
    const formattedDate = formatDate(date);
    
    // Skip the API call if we already have rates for this date
    if (formattedDate === lastRequestedDate && rates !== null) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setLastRequestedDate(formattedDate);
    
    try {
      // Use getExchangeRates instead of forceRefreshRates to benefit from 24-hour caching
      const { data: exchangeRates, error: apiError } = await currencyAPI.getExchangeRates();
      
      if (apiError || !exchangeRates || exchangeRates.length === 0) {
        throw new Error('Failed to fetch exchange rates');
      }
      
      // Process the rates
      processRates(exchangeRates, formattedDate);
    } catch (err) {
      
      setError('Failed to fetch currency rates. Please check your internet connection and try again.');
      
      // We don't set default rates - instead show an error
      setRates(null);
    } finally {
      setLoading(false);
    }
  }, [rates, lastRequestedDate]);
  
  // Helper to process and extract rates from API response
  const processRates = (exchangeRates: any[], formattedDate: string) => {
    if (!exchangeRates || exchangeRates.length === 0) {
      throw new Error('No exchange rates returned from API');
    }
    
    // Extract the USD-to-TRY and EUR-to-TRY rates
    const usdToTry = exchangeRates.find(
      r => r.base_currency === 'USD' && r.target_currency === 'TRY'
    );
    
    const eurToTry = exchangeRates.find(
      r => r.base_currency === 'EUR' && r.target_currency === 'TRY'
    );
    
    // If these rates aren't found, try the inverse
    const tryToUsd = exchangeRates.find(
      r => r.base_currency === 'TRY' && r.target_currency === 'USD'
    );
    
    const tryToEur = exchangeRates.find(
      r => r.base_currency === 'TRY' && r.target_currency === 'EUR'
    );
    
    // Ensure we have at least one set of rates
    if ((!usdToTry && !tryToUsd) || (!eurToTry && !tryToEur)) {
      throw new Error('Required currency pairs not found in API response');
    }
    
    // Calculate the rates (prefer direct rates, fallback to reciprocal)
    const usdRate = usdToTry?.rate || (tryToUsd ? 1 / tryToUsd.rate : null);
    const eurRate = eurToTry?.rate || (tryToEur ? 1 / tryToEur.rate : null);
    
    if (!usdRate || !eurRate) {
      throw new Error('Could not calculate exchange rates from API data');
    }
    
    const calculatedRates = {
      usd: usdRate,
      eur: eurRate,
      date: formattedDate
    };
    
    setRates(calculatedRates);
  };

  return { 
    rates, 
    loading, 
    error, 
    fetchRates,
    isRatesAvailable: rates !== null 
  };
}; 